<template>
  <BbDashboardCard class="amp-card" fill-width>
    <template #image>
      <div class="thumbnail-wrapper" @click="onSelectCreation(creation)" v-if="thumbnail">
        <div class="email-label">Email {{ index }}</div>
        <v-img :src="thumbnail" width="100%" max-height="168" min-height="250" contain :style="backgroundStyle" />
      </div>
      <div v-else class="missing-thumbnail-wrapper" @click="onSelectCreation(creation)">
        <div class="email-label">Email {{ index }}</div>
        <BbIcon class="emails-icon" name="emails" />
      </div>
    </template>

    <div class="bb-dashboard-card__row">
      <div class="amp-labels">DRAFT</div>

      <div class="last-updated">{{ modifiedAt }}</div>
    </div>

    <EditableField
      :value="title"
      :isEditing="isEditableTitle === creation.id"
      @update="(value) => updateTitle(creation.id, value)"
      @cancel="cancelEdit"
    />

    <template #footer>
      <div class="bb-dashboard-card__row" data-app>
        <BbTooltip v-bind="{ placement: 'top', offset: 4, isDisabled: false, content: t('common.duplicate') }">
          <BbBtn size="small" icon="copynode" icon-size="small" is-icon-button @click="onDuplicate(creation.id)" />
        </BbTooltip>
        <div>
          <BbTooltip v-bind="{ placement: 'top', offset: 4, isDisabled: false, content: t('common.translate') }">
            <BbBtn
              size="small"
              icon="translate"
              icon-size="small"
              is-icon-button
              @click="onTranslateEmail(creation.id, creation.title)"
            />
          </BbTooltip>
          <BbTooltip v-bind="{ placement: 'top', offset: 4, isDisabled: false, content: t('common.download') }">
            <BbBtn
              size="small"
              :icon="isDownloadInProgress ? 'spin' : 'download'"
              icon-size="small"
              is-icon-button
              :is-disabled="isDownloadInProgress"
              @click="onDownload(creation.id)"
            />
          </BbTooltip>
          <BbBtn
            size="small"
            icon="edit"
            icon-type="outlined"
            is-icon-button
            without-background
            @click="setEditableTitle(creation.id)"
          />
          <BbBtn size="small" icon="delete" icon-size="small" is-icon-button @click="confirmDeletePopupOpened = true" />
        </div>
      </div>
    </template>
  </BbDashboardCard>
  <WarningPopup
    :popup-opened="confirmDeletePopupOpened"
    title-key="common.delete"
    warning-message-key="modals.warningDeleteMessage"
    confirm-text-key="modals.confirmDeleteText"
    @update:open="confirmDeletePopupOpened = false"
    @execute:action="confirmDelete(creation.id)"
  />
</template>

<script>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { TACTIC_TYPES } from '~/constants/tacticTypes';
import { Routes } from '~/constants/routes';
import { TRANSLATION_TYPE } from '~/constants/documents';
import { IsDownloadInProgress } from '~/constants/getters';

import EditableField from '~/components/common/EditableField.vue';
import WarningPopup from '~/components/common/WarningPopup.vue';

import useDocumentEditor from '~/composables/useDocumentEditor';
import useDocuments from '~/composables/useDocuments';
import useModals from '~/composables/useModals';

import { calculatePassedTime } from '~/utils/date-utils';

export default {
  components: { WarningPopup, EditableField },
  props: {
    creation: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const router = useRouter();
    const store = useStore();
    const { t } = useI18n();

    const { updateAuthoredDocumentResource } = useDocumentEditor();
    const { downloadAuthoredDocument, deleteAuthoredDocumentById, duplicateAuthoredDocument } = useDocuments();
    const { showTranslationModal } = useModals();

    const isEditableTitle = ref(null);
    const title = ref(props.creation?.title || 'Ai generated headline goes here');
    const oldTitle = ref(props.creation?.title || 'Ai generated headline goes here');
    const confirmDeletePopupOpened = ref(false);
    const thumbnail = computed(() => props.creation?.url);
    const backgroundStyle = computed(() => ({ background: `url(${thumbnail.value}) lightgray 50% / cover no-repeat` }));
    const modifiedAt = computed(() =>
      props.creation?.modifiedAt ? calculatePassedTime(props.creation?.modifiedAt) : null,
    );
    const isDownloadInProgress = computed(() =>
      store.getters[`documentStore/${IsDownloadInProgress}`](props.creation.id),
    );

    const confirmDelete = async (creationId) => {
      await deleteAuthoredDocumentById(creationId);
      confirmDeletePopupOpened.value = false;
    };

    const cancelEdit = () => {
      title.value = oldTitle.value;
      isEditableTitle.value = null;
    };

    const setEditableTitle = (id) => {
      isEditableTitle.value = id;
    };

    const onDownload = async (id) => {
      await downloadAuthoredDocument(id);
    };

    const onDuplicate = async (id) => await duplicateAuthoredDocument(id);

    const onTranslateEmail = async (id, name) => {
      showTranslationModal(`email/${id}/content.mjml`, name || 'Email', TRANSLATION_TYPE.email);
    };

    const onSelectCreation = (creation) =>
      router.push({
        name: Routes.Authoring.name,
        params: { conversationId: `${creation.id}` },
      });

    const updateTitle = async (id, value) => {
      await updateAuthoredDocumentResource(
        id,
        {
          title: value,
          content: props.creation.content,
          tacticType: TACTIC_TYPES.email,
        },
        false,
      );
      title.value = value;
      oldTitle.value = value;
      isEditableTitle.value = null;
    };

    return {
      backgroundStyle,
      onSelectCreation,
      onDuplicate,
      setEditableTitle,
      onDownload,
      isEditableTitle,
      cancelEdit,
      title,
      updateTitle,
      thumbnail,
      t,
      modifiedAt,
      confirmDelete,
      confirmDeletePopupOpened,
      isDownloadInProgress,
      onTranslateEmail,
    };
  },
};
</script>

<style lang="scss">
.email-label {
  width: fit-content;
  background: $bb-neutral-0;
  padding: 0 10px;
  border-radius: 5px 100px 100px 100px;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 2;
  height: $bb-spacing-4;
  min-width: $bb-spacing-14;
  line-height: $bb-spacing-4;
  font-size: 11px;
}

.amp-card {
  @include bb-base-font-family;
  position: relative;
  flex: 1 1 271px;
  max-width: 321px !important;
  width: 291px !important;
  height: 288px !important; // use this when footer will be added back
  //height: 262px !important;

  .amp-labels {
    display: flex;
    flex-direction: row;
    padding: 0px 8px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    width: fit-content;
    background: #e39250;
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.25px;
    color: $bb-neutral-0;
  }

  .last-updated {
    @include bb-regular-text-styles('xs');
    color: $bb-neutral-2;
  }

  .thumbnail-wrapper {
    position: relative;
    overflow: hidden;
    border-radius: 3px 3px 0 0;
    cursor: pointer;

    :deep(.v-img__img) {
      backdrop-filter: blur(12.5px);
      padding-top: 7px;
    }
  }

  .missing-thumbnail-wrapper {
    position: relative;
    background: $bb-neutral-5;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    height: 168px;

    .emails-icon {
      font-size: 66px;
    }
  }

  .bb-dashboard-card__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;

    .bb-btn.primary {
      width: 32px;
      height: 32px;
      border-radius: 12px;
      background: $bb-neutral-6 !important;
      color: #1c1c1d !important;

      &:hover {
        background: $bb-neutral-6 !important;
      }

      &:nth-of-type(2) {
        background: $bb-interface-5 !important;
        color: $bb-neutral-0 !important;
        margin-left: $bb-spacing-1;
      }
    }
  }

  &:hover {
    box-shadow: 0px 5px 8px rgba(0, 0, 0, 0.1);
  }
}
</style>
